import React, { useState } from 'react';

interface Problem {
  id: number;
  title: string;
  category: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  approach: string;
  timeComplexity: string;
  spaceComplexity: string;
  tags: string[];
  code: string;
}

const PROBLEMS: Problem[] = [
  {
    id: 1,
    title: 'Flatten Deeply Nested Object',
    category: 'Objects & Recursion',
    difficulty: 'Medium',
    approach:
      'Recursively traverse each key. If the value is a plain object (not array, not null), recurse with a compound key prefix. Otherwise assign directly to the flat result object.',
    timeComplexity: 'O(n)',
    spaceComplexity: 'O(d) — d = max depth',
    tags: ['Recursion', 'Objects', 'Real-world'],
    code: `function flattenObject(
  obj: Record<string, any>,
  prefix = ''
): Record<string, any> {
  return Object.keys(obj).reduce((acc, key) => {
    const fullKey = prefix ? \`\${prefix}.\${key}\` : key;
    const val = obj[key];
    if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
      Object.assign(acc, flattenObject(val, fullKey));
    } else {
      acc[fullKey] = val;
    }
    return acc;
  }, {} as Record<string, any>);
}`,
  },
  {
    id: 2,
    title: 'LRU Cache Implementation',
    category: 'Data Structures',
    difficulty: 'Hard',
    approach:
      'Use a Map (insertion-ordered) as a doubly-linked list substitute. On get/put, delete then re-insert the key so it moves to the tail (most recently used). When capacity is exceeded, delete the Map\'s first (oldest) entry.',
    timeComplexity: 'O(1) get & put',
    spaceComplexity: 'O(capacity)',
    tags: ['Map', 'Cache', 'LeetCode 146'],
    code: `class LRUCache {
  private capacity: number;
  private cache: Map<number, number>;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.cache = new Map();
  }

  get(key: number): number {
    if (!this.cache.has(key)) return -1;
    const val = this.cache.get(key)!;
    this.cache.delete(key);
    this.cache.set(key, val); // refresh to tail
    return val;
  }

  put(key: number, value: number): void {
    if (this.cache.has(key)) this.cache.delete(key);
    else if (this.cache.size >= this.capacity) {
      this.cache.delete(this.cache.keys().next().value!);
    }
    this.cache.set(key, value);
  }
}`,
  },
  {
    id: 3,
    title: 'Debounce Implementation',
    category: 'JS Patterns',
    difficulty: 'Easy',
    approach:
      'Return a wrapper function that clears any pending timer on each call and sets a new one. The original function only fires after the delay has elapsed with no further calls.',
    timeComplexity: 'O(1)',
    spaceComplexity: 'O(1)',
    tags: ['Closures', 'Timers', 'React'],
    code: `function debounce<T extends (...args: any[]) => void>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}`,
  },
];

const DIFFICULTY_COLORS: Record<Problem['difficulty'], string> = {
  Easy: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/5',
  Medium: 'text-amber-400 border-amber-500/30 bg-amber-500/5',
  Hard: 'text-red-400 border-red-500/30 bg-red-500/5',
};

const ProblemSection: React.FC = () => {
  const [active, setActive] = useState<number>(1);

  const problem = PROBLEMS.find((p) => p.id === active)!;

  return (
    <section id="dsa" className="py-28 bg-[#0d0d14]">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="mb-16">
          <p className="text-emerald-400 font-mono text-sm tracking-widest uppercase mb-3">
            Problem Solving
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-white">DSA & Patterns</h2>
          <p className="text-slate-400 mt-3 max-w-xl">
            Real problems solved with clean TypeScript — approach, complexity, and production-ready
            code.
          </p>
        </div>

        {/* Tab selector */}
        <div className="flex flex-wrap gap-3 mb-8">
          {PROBLEMS.map((p) => (
            <button
              key={p.id}
              onClick={() => setActive(p.id)}
              className={`px-4 py-2 rounded-lg border text-sm font-semibold transition-all duration-200 ${
                active === p.id
                  ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300'
                  : 'bg-[#13131e] border-slate-800 text-slate-400 hover:border-slate-600 hover:text-slate-200'
              }`}
            >
              {p.title}
            </button>
          ))}
        </div>

        {/* Problem card */}
        <div className="rounded-2xl bg-[#13131e] border border-slate-800 overflow-hidden">
          {/* Card header */}
          <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-5 border-b border-slate-800">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <span className="text-xs font-mono text-slate-500 uppercase tracking-wider">
                  {problem.category}
                </span>
                <span
                  className={`px-2 py-0.5 rounded-full border text-xs font-mono ${
                    DIFFICULTY_COLORS[problem.difficulty]
                  }`}
                >
                  {problem.difficulty}
                </span>
              </div>
              <h3 className="text-xl font-bold text-white">{problem.title}</h3>
            </div>
            <div className="flex gap-2 flex-wrap">
              {problem.tags.map((t) => (
                <span
                  key={t}
                  className="px-2.5 py-1 text-xs bg-slate-800 text-slate-400 rounded-md font-mono"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-800">
            {/* Left: approach + complexity */}
            <div className="p-6">
              <h4 className="text-xs font-mono text-slate-500 uppercase tracking-widest mb-3">
                Approach
              </h4>
              <p className="text-slate-300 text-sm leading-relaxed mb-6">{problem.approach}</p>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-lg bg-slate-900/60 border border-slate-800">
                  <div className="text-xs text-slate-500 font-mono mb-1">Time</div>
                  <div className="text-sm font-mono text-cyan-400">{problem.timeComplexity}</div>
                </div>
                <div className="p-3 rounded-lg bg-slate-900/60 border border-slate-800">
                  <div className="text-xs text-slate-500 font-mono mb-1">Space</div>
                  <div className="text-sm font-mono text-purple-400">{problem.spaceComplexity}</div>
                </div>
              </div>
            </div>

            {/* Right: code */}
            <div className="p-6">
              <h4 className="text-xs font-mono text-slate-500 uppercase tracking-widest mb-3">
                Solution
              </h4>
              <pre className="text-xs font-mono text-slate-300 leading-relaxed overflow-x-auto bg-slate-950/60 rounded-lg p-4 border border-slate-800/60">
                <code>{problem.code}</code>
              </pre>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
